import { useEffect, useRef, useState } from "react";
import { Camera, Copy, History, Loader2, Package, Pencil, Trash2, Check, ChevronLeft, ChevronRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { PqSyncStatus } from "./PqSyncStatus";
import { photoThumb, photoThumbBackend } from "@/lib/photoUrl";

const STORE_KEY = "pq_mobile_card_fields";
const DEFAULT_FIELDS = ["price", "size", "factory", "quote_customer", "quote_date", "salesperson"];

const readFields = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORE_KEY) || "null");
    return Array.isArray(raw) && raw.length ? raw : DEFAULT_FIELDS;
  } catch {
    return DEFAULT_FIELDS;
  }
};

/**
 * 2026-06 · 采购报价 手机端卡片列表（采购角色主用）。
 *  · 卡片显示字段可自选（本机记忆）
 *  · 相机按钮：拍照直接给该产品编号换图（可选一键抠图）
 *  · 翻页：加载更多 + 滚到底自动加载 + 上一页/下一页（用户要求三种都留）
 */
export function PqMobileList({
  rows, loading, columns, cellValue,
  canEditRow, canDuplicate, isSameDayOwn,
  onEdit, onDuplicate, onDelete, onHistory, onPhotoUploaded,
  page, totalPages, setPage, hasMore, onLoadMore, loadingMore,
  fieldSheetOpen, setFieldSheetOpen,
}) {
  const { t } = useLanguage();
  const [fields, setFields] = useState(readFields);
  const [busyPhoto, setBusyPhoto] = useState(null);
  const [freshPhotos, setFreshPhotos] = useState({});   // 上传成功后先本地换图
  const sentinel = useRef(null);

  const pickable = columns.filter((c) => c.key !== "product_photo" && c.key !== "item_number" && c.key !== "name_cn");

  const toggleField = (key) => {
    setFields((f) => {
      const next = f.includes(key) ? f.filter((k) => k !== key) : [...f, key];
      localStorage.setItem(STORE_KEY, JSON.stringify(next));
      return next;
    });
  };

  // 滚动到底自动加载下一批
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !hasMore) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && !loadingMore) onLoadMore();
    }, { rootMargin: "240px" });
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore]);

  const uploadPhoto = async (row, fileList, cutout) => {
    const file = fileList?.[0];
    if (!file) return;
    setBusyPhoto(row.id);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("cutout", cutout ? "true" : "false");
      const { data } = await api.post(`/procurement-quotes/${row.id}/photo`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("pq.photoUploaded"));
      setFreshPhotos((m) => ({ ...m, [row.id]: data.photo_url }));
      onPhotoUploaded?.(row, data.photo_url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyPhoto(null);
    }
  };

  return (
    <div className="md:hidden space-y-2.5" data-testid="pq-mobile-list">
      {loading && rows.length === 0 && (
        <p className="text-center text-sm text-muted-foreground py-10" data-testid="pq-mobile-loading">…</p>
      )}
      {!loading && rows.length === 0 && (
        <p className="text-center text-sm text-muted-foreground py-12" data-testid="pq-mobile-empty">{t("pq.empty")}</p>
      )}

      {rows.map((r) => {
        const editable = canEditRow(r);
        const rowKey = r.fm_record_id || r.id;
        const photo = freshPhotos[r.id] || r.product_composite_photo_url || r.product_photo_url;
        return (
          <div key={rowKey} className="bg-card border border-border rounded-lg p-2.5"
            data-testid={`pq-mobile-card-${rowKey}`}>
            <div className="flex gap-2.5">
              <div className="relative w-[84px] h-[84px] shrink-0 rounded-md border border-border bg-white overflow-hidden">
                {photo ? (
                  <img src={photoThumb(photo, "sm")} alt={r.item_number || ""}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      const i = e.currentTarget;
                      if (i.dataset.f !== "1") { i.dataset.f = "1"; i.src = photoThumbBackend(photo, "sm"); }
                    }}
                    data-testid={`pq-mobile-photo-${rowKey}`} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package size={22} className="text-muted-foreground" />
                  </div>
                )}
                {editable && (
                  <label className="absolute bottom-0 inset-x-0 h-7 bg-black/55 text-white flex items-center justify-center gap-1 text-[10px] active:bg-black/75"
                    data-testid={`pq-mobile-camera-${rowKey}`}>
                    <input type="file" accept="image/*" capture="environment" className="hidden"
                      onChange={(e) => { uploadPhoto(r, e.target.files, false); e.target.value = ""; }} />
                    {busyPhoto === r.id ? <Loader2 size={13} className="animate-spin" /> : <Camera size={13} />}
                    {t("pq.takePhoto")}
                  </label>
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-sm font-bold truncate">{r.item_number || "-"}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{r.name_cn || "-"}</p>
                  </div>
                  {r.tag && (
                    <span className="shrink-0 px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-900 text-[10px] font-semibold">
                      {r.tag}
                    </span>
                  )}
                </div>
                <div className="mt-1.5 grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px]">
                  {fields.map((key) => {
                    const col = columns.find((c) => c.key === key);
                    if (!col) return null;
                    const v = cellValue(r, col);
                    if (!v || v === "-") return null;
                    return (
                      <div key={key} className="flex gap-1 min-w-0" data-testid={`pq-mobile-field-${rowKey}-${key}`}>
                        <span className="text-muted-foreground shrink-0">{t(col.label)}:</span>
                        <span className={`truncate ${col.num ? "font-mono font-semibold" : ""}`}>{v}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <PqSyncStatus record={r} prefix="pq-mobile-sync" />
            <div className="mt-2 pt-2 border-t border-border flex items-center gap-1">
              <Button variant="outline" size="sm" className="h-9 flex-1 text-xs" disabled={!editable}
                onClick={() => onEdit(r)} data-testid={`pq-mobile-edit-${rowKey}`}>
                <Pencil size={13} /> {t("common.edit")}
              </Button>
              <Button variant="outline" size="sm" className="h-9 px-3" disabled={!canDuplicate()}
                onClick={() => onDuplicate(r)} data-testid={`pq-mobile-duplicate-${rowKey}`}>
                <Copy size={13} />
              </Button>
              <Button variant="outline" size="sm" className="h-9 px-3" disabled={!r.item_number}
                onClick={() => onHistory(r.item_number)} data-testid={`pq-mobile-history-${rowKey}`}>
                <History size={13} />
              </Button>
              <Button variant="outline" size="sm" className="h-9 px-3 text-destructive"
                disabled={!isSameDayOwn(r)} onClick={() => onDelete(r)}
                data-testid={`pq-mobile-delete-${rowKey}`}>
                <Trash2 size={13} />
              </Button>
            </div>
          </div>
        );
      })}

      <div ref={sentinel} />

      {hasMore && (
        <Button variant="outline" className="w-full h-11" onClick={onLoadMore} disabled={loadingMore}
          data-testid="pq-mobile-load-more">
          {loadingMore ? <Loader2 size={15} className="animate-spin" /> : null} {t("pq.loadMore")}
        </Button>
      )}

      <div className="flex items-center justify-center gap-3 pt-1 pb-2">
        <Button variant="outline" size="sm" className="h-9" disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)} data-testid="pq-mobile-prev-page">
          <ChevronLeft size={14} /> {t("pq.prev")}
        </Button>
        <span className="text-xs font-mono text-muted-foreground" data-testid="pq-mobile-page-info">
          {page} / {totalPages}
        </span>
        <Button variant="outline" size="sm" className="h-9" disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)} data-testid="pq-mobile-next-page">
          {t("pq.next")} <ChevronRight size={14} />
        </Button>
      </div>

      {/* 卡片显示字段设置（本机记忆） */}
      <Sheet open={fieldSheetOpen} onOpenChange={setFieldSheetOpen}>
        <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto rounded-t-xl"
          data-testid="pq-mobile-field-sheet">
          <SheetHeader>
            <SheetTitle className="text-base">{t("pq.mobileFieldsTitle")}</SheetTitle>
          </SheetHeader>
          <ul className="grid grid-cols-2 gap-1.5 py-3">
            {pickable.map((col) => {
              const on = fields.includes(col.key);
              return (
                <li key={col.key}>
                  <button type="button" onClick={() => toggleField(col.key)}
                    className={`w-full h-10 px-2.5 rounded-md border text-xs text-left flex items-center gap-1.5 transition-colors ${on ? "border-brand bg-brand/10 text-brand font-semibold" : "border-border"}`}
                    data-testid={`pq-mobile-field-toggle-${col.key}`}>
                    {on ? <Check size={13} className="shrink-0" /> : <span className="w-[13px] shrink-0" />}
                    <span className="truncate">{t(col.label)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          <SheetFooter>
            <Button className="w-full h-11 bg-brand hover:bg-brand-hover"
              onClick={() => setFieldSheetOpen(false)} data-testid="pq-mobile-field-done">
              {t("common.done")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
